import React, {
  createContext,
  ReactNode,
  useState,
  SetStateAction,
  Dispatch,
  useContext,
} from 'react';
import {
  ScrollableSource,
  useScrollableSource,
  UserContext,
  NavigationContext,
  EvidenceStateAction,
} from '@eas/common-web';
import { Area, Source } from './overview-map-types';
import { EvidenceAPI, Permission, EvidenceUrl } from '../../enums';
import { IrzFacility, OvzFacility, ProductionSite } from '../../models';
import {
  MarkerPopupConfig,
  useMarkerPopupFactory,
} from '../../components/map/hook/marker-popup-factory';

import { IrzFacilitiesMarkerPopupBody } from '../irz-facility/irz-facilities-map';
import { fetchIrzFacility } from '../irz-facility/irz-facilities-api';
import {
  fetchOvzFacility,
  useLandPlotTypes,
} from '../ovz-facility/ovz-facilities-api';
import { fetchProductionSite } from '../production-site/production-sites-api';
import { OvzFacilitiesMarkerPopupBody } from '../ovz-facility/ovz-facilities-map';
import { ProductionSitePopup } from '../production-site/production-sites-map';

export interface OverviewMapContextType {
  irzFacilitySource: ScrollableSource<IrzFacility>;
  ovzFacilitySource: ScrollableSource<OvzFacility>;
  productionSiteSource: ScrollableSource<ProductionSite>;
  irzFacilityPopupConfig: MarkerPopupConfig<IrzFacility>;
  ovzFacilityPopupConfig: MarkerPopupConfig<OvzFacility>;
  productionSitePopupConfig: MarkerPopupConfig<ProductionSite>;
  source: Source[];
  setSource: Dispatch<SetStateAction<Source[]>>;
  defaultValues: { [key: string]: Source };
  withFilters: boolean;
  setWithFilters: Dispatch<SetStateAction<boolean>>;
  canSeeIrzFacilities: boolean;
  canSeeOvzFacilities: boolean;
  canSeeProductionSites: boolean;
}

export const OverviewMapContext = createContext<OverviewMapContextType>(
  undefined as any
);

export function OverviewMapProvider({ children }: { children: ReactNode }) {
  const [withFilters, setWithFilters] = useState(false);
  const { hasPermission } = useContext(UserContext);
  const { navigate } = useContext(NavigationContext);

  const landPlotTypes = useLandPlotTypes();

  const canSeeIrzFacilities =
    hasPermission(Permission.IrzFacility.IRZ_LIST_ALL) ||
    hasPermission(Permission.IrzFacility.IRZ_LIST_OWN);

  const canSeeOvzFacilities =
    hasPermission(Permission.OvzFacility.OVZ_LIST_ALL) ||
    hasPermission(Permission.OvzFacility.OVZ_LIST_OWN);

  const canSeeProductionSites = hasPermission(
    Permission.ProductionSite.PRODUCTION_SITE_LIST
  );

  const irzFacilityPopupConfig = useMarkerPopupFactory<IrzFacility>({
    fetchData: (marker) => fetchIrzFacility(marker?.id ?? '').json(),
    PopupBodyComponent: (props) =>
      IrzFacilitiesMarkerPopupBody({
        ...props,
        setActive: (id: string) => {
          navigate(EvidenceUrl.IRZ_FACILITIES + '/' + id, false, {
            action: EvidenceStateAction.SHOW_ITEM,
            data: id,
          });
        },
      }),
  });

  const ovzFacilityPopupConfig = useMarkerPopupFactory<OvzFacility>({
    fetchData: (marker) => fetchOvzFacility(marker?.id ?? '').json(),
    PopupBodyComponent: (props) =>
      OvzFacilitiesMarkerPopupBody({
        ...props,
        data: {
          ...props.data,
          landPlotTypeLabel:
            landPlotTypes.items.find(
              (i) => i.id === (props.data as OvzFacility)?.landPlotType
            )?.name ?? '-',
        },
        setActive: (id: string) => {
          navigate(EvidenceUrl.OVZ_FACILITIES + '/' + id, false, {
            action: EvidenceStateAction.SHOW_ITEM,
            data: id,
          });
        },
      }),
  });

  const productionSitePopupConfig = useMarkerPopupFactory<ProductionSite>({
    fetchData: (marker) => fetchProductionSite(marker.id ?? '').json(),
    PopupBodyComponent: (props) =>
      ProductionSitePopup({
        ...props,
        setActive: (id: string) => {
          navigate(EvidenceUrl.PRODUCTION_SITES + '/' + id, false, {
            action: EvidenceStateAction.SHOW_ITEM,
            data: id,
          });
        },
      }),
  });

  const defaultValues = {
    [Area.IRZ_FACILITY]: {
      api: EvidenceAPI.IRZ_FACILITIES,
      type: Area.IRZ_FACILITY,
      filters: [],
      shouldLoad: canSeeIrzFacilities,
    },
    [Area.OVZ_FACILITY]: {
      api: EvidenceAPI.OVZ_FACILITIES,
      type: Area.OVZ_FACILITY,
      filters: [],
      shouldLoad: canSeeOvzFacilities,
    },
    [Area.PRODUCTION_SITE]: {
      api: EvidenceAPI.PRODUCTION_SITES,
      type: Area.PRODUCTION_SITE,
      filters: [],
      shouldLoad: canSeeProductionSites,
    },
  };

  //const [source, setSource] = useState<Source[]>(Object.values(defaultValues));

  const [source, setSource] = useState<Source[]>([
    defaultValues[Area.IRZ_FACILITY],
  ]);

  const irzFacilitySource = useScrollableSource<IrzFacility>({
    url: EvidenceAPI.IRZ_FACILITIES + '/list',
  });

  const ovzFacilitySource = useScrollableSource<OvzFacility>({
    url: EvidenceAPI.OVZ_FACILITIES + '/list',
  });

  const productionSiteSource = useScrollableSource<ProductionSite>({
    url: EvidenceAPI.PRODUCTION_SITES + '/list',
  });

  return (
    <OverviewMapContext.Provider
      value={{
        irzFacilitySource,
        ovzFacilitySource,
        productionSiteSource,
        source,
        setSource,
        defaultValues,
        withFilters,
        setWithFilters,
        canSeeIrzFacilities,
        canSeeOvzFacilities,
        canSeeProductionSites,
        irzFacilityPopupConfig,
        ovzFacilityPopupConfig,
        productionSitePopupConfig,
      }}
    >
      {children}
    </OverviewMapContext.Provider>
  );
}
